import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useToken } from '../../hooks/useToken';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import {
  createManagedKeyAccount,
  confirmSnowflakeConnection,
  deleteSnowflakeAccount,
  selectManagedKeyData,
  selectDialogError,
  selectValidationStatus,
  clearDialogError,
  clearManagedKeyData,
} from '../../reducers/snowflakeAccountsSlice';
import { AccountValidationInput } from './AccountValidationInput';
import { SQLSetupSection } from './SQLSetupSection';
import { AccountSetupProgress } from './AccountSetupProgress';
import Button from '../controls/Button';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { generateDefaultUsername } from '../../utilities/usernameGenerator';
import { generateCompleteSqlBlock } from '../../utilities/snowflakeUtils';

interface ManagedKeyFormProps {
  onSuccess: (accountId: number) => void;
  onCancel: () => void;
}

/**
 * Multi-step form for creating a Snowflake account with managed key authentication
 *
 * Flow (3 main steps):
 * 1. Account identifier input with real-time validation
 * 2. Display SQL commands with copy functionality and Snowsight button
 * 3. Test connection and show success
 *
 * State management:
 * - Uses local state for: accountIdentifier, currentStep
 * - Uses Redux for: validation status, managed key data, account creation
 * - Handles loading states during account creation and connection testing
 *
 * @param onSuccess - Callback when account is successfully created and tested
 * @param onCancel - Callback to cancel the flow
 */
export function ManagedKeyForm({ onSuccess, onCancel }: ManagedKeyFormProps) {
  const dispatch = useAppDispatch();
  const token = useToken();
  const currentUser = useCurrentUser();

  // Redux state
  const managedKeyData = useAppSelector(selectManagedKeyData);
  const dialogError = useAppSelector(selectDialogError);
  const validationStatus = useAppSelector(selectValidationStatus);

  // Generate username from user's email
  const userEmail = currentUser.emails.nodes[0]?.email || '';
  const defaultUsername = generateDefaultUsername(userEmail);

  // Local state
  const [accountIdentifier, setAccountIdentifier] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [sqlExecuted, setSqlExecuted] = useState(false);
  const [username] = useState<string>(defaultUsername);
  const [warehouse] = useState<string>('DEMO_BULD_WH');
  const [role] = useState<string>('ACCOUNTADMIN');

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const connectionSuccessRef = useRef<boolean>(false);
  const accountIdRef = useRef<number | null>(null);

  // Clear error when component mounts
  useEffect(() => {
    dispatch(clearDialogError());
  }, [dispatch]);

  // Scroll to top when error appears
  useEffect(() => {
    if (dialogError && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [dialogError]);

  // Handle validation success
  const handleValidationSuccess = useCallback((validatedIdentifier: string) => {
    setAccountIdentifier(validatedIdentifier);
  }, []);

  // Handle "Next" button click - create account and generate key pair
  const handleNext = async () => {
    if (validationStatus !== 'valid' || !accountIdentifier) {
      return;
    }

    setIsCreatingAccount(true);
    try {
      const response = await dispatch(
        createManagedKeyAccount({ token, accountIdentifier, username, warehouse, role }),
      ).unwrap();
      accountIdRef.current = response.account.id;
      setCurrentStep(2);
    } catch (err) {
      console.error('Failed to create account:', err);
    } finally {
      setIsCreatingAccount(false);
    }
  };

  // Handle test connection
  const handleTestConnection = async () => {
    if (!accountIdRef.current) {
      return;
    }

    setIsTestingConnection(true);
    try {
      await dispatch(
        confirmSnowflakeConnection({
          token,
          accountId: accountIdRef.current.toString(),
        }),
      ).unwrap();
      setConnectionSuccess(true);
      connectionSuccessRef.current = true; // Update ref for cleanup
      setCurrentStep(3);

      // Wait a moment to show success message, then call onSuccess with account ID
      setTimeout(() => {
        if (accountIdRef.current) {
          onSuccess(accountIdRef.current);
        }
      }, 3000);
    } catch (err) {
      console.error('Failed to test connection:', err);
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Handle cancel - delete account if not successfully connected
  const handleCancel = async () => {
    if (isCreatingAccount || isTestingConnection) {
      return;
    }

    // If account was created but not successfully connected, delete it
    if (accountIdRef.current && !connectionSuccessRef.current) {
      try {
        await dispatch(
          deleteSnowflakeAccount({
            token,
            accountId: accountIdRef.current.toString(),
          }),
        ).unwrap();
      } catch (err) {
        console.error('Failed to delete incomplete account:', err);
      }
    }

    onCancel();
  };

  // Cleanup on unmount - delete account if not successfully connected and clear managed key data
  useEffect(() => {
    return () => {
      // Only delete if account was created but not successfully connected
      // Use refs to get the latest values at cleanup time
      if (accountIdRef.current && !connectionSuccessRef.current) {
        dispatch(
          deleteSnowflakeAccount({
            token,
            accountId: accountIdRef.current.toString(),
          }),
        ).catch((err) => {
          console.error('Failed to delete incomplete account on unmount:', err);
        });
      }
      // Always clear managed key data on unmount
      dispatch(clearManagedKeyData());
    };
    // Empty deps array - cleanup only runs on unmount, not when dependencies change
  }, []);

  return (
    <div className="space-y-6" ref={containerRef}>
      {/* Progress Indicator */}
      <AccountSetupProgress currentStep={currentStep} />

      {/* Error Display */}
      {dialogError && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{dialogError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Account Validation */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Step 1: Validate Account</h3>
            <p className="mt-1 text-sm text-gray-600">
              Enter your Snowflake account identifier to begin the setup process.
            </p>
          </div>

          <AccountValidationInput onChange={setAccountIdentifier} onValidationSuccess={handleValidationSuccess} />

          <div className="flex justify-end space-x-3 pt-4">
            <Button intent="light" size="large" onClick={handleCancel} disabled={isCreatingAccount}>
              Cancel
            </Button>
            <Button
              intent="primary"
              size="large"
              onClick={handleNext}
              disabled={validationStatus !== 'valid' || !accountIdentifier}
              loading={isCreatingAccount}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: SQL Setup */}
      {currentStep === 2 && managedKeyData && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Step 2: Configure Access</h3>
            <p className="mt-1 text-sm text-gray-600">
              Run the following SQL commands in your Snowflake account to grant access.
            </p>
          </div>

          <SQLSetupSection
            setupSQL={generateCompleteSqlBlock(username, warehouse, role, managedKeyData.publicKey)}
            accountIdentifier={accountIdentifier}
            sqlExecuted={sqlExecuted}
            onSqlExecutedChange={setSqlExecuted}
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button intent="light" size="large" onClick={handleCancel} disabled={isTestingConnection}>
              Cancel
            </Button>
            <Button
              intent="primary"
              size="large"
              onClick={handleTestConnection}
              disabled={!sqlExecuted}
              loading={isTestingConnection}
              aria-label="Test Snowflake connection"
            >
              Test Connection
            </Button>
          </div>
          {!sqlExecuted && (
            <p className="text-sm text-gray-500 text-right" role="status" aria-live="polite">
              Please confirm you have run the SQL commands before testing the connection.
            </p>
          )}
        </div>
      )}

      {/* Step 3: Success */}
      {currentStep === 3 && connectionSuccess && (
        <div className="space-y-6">
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Connection Successful!</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Your Snowflake account has been successfully connected and is ready to use.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
